import posthog from "posthog-js";

const DEFAULT_POSTHOGS_HOST = "https://eu.i.posthog.com";
const DEFAULTS_SNAPSHOT = "2026-01-30";
const ORGANIZATION_GROUP_TYPE = "organization";

let initialized = false;
let lastIdentifiedUserKey: string | null = null;
let lastIdentifiedOrganizationKey: string | null = null;

type EventProperties = Record<string, string | number | boolean | null | undefined>;

type ProductAnalyticsUser = {
  distinctId: string;
  email: string;
  name: string;
  organizationId: number | null;
  organizationName: string | null;
  role: string;
  verified: boolean;
};

type ProductAnalyticsOrganization = {
  id: string;
  name: string;
};

export function initializeProductAnalytics() {
  if (initialized) return;

  const key = import.meta.env.VITE_POSTHOG_KEY;
  if (!key) return;

  posthog.init(key, {
    api_host: import.meta.env.VITE_POSTHOG_HOST ?? DEFAULT_POSTHOGS_HOST,
    autocapture: false,
    capture_pageview: false,
    defaults: DEFAULTS_SNAPSHOT,
  });

  initialized = true;
}

export function isProductAnalyticsEnabled() {
  return initialized;
}

export function captureProductEvent(event: string, properties: EventProperties = {}) {
  if (!initialized) return;

  posthog.capture(event, compactProperties(properties));
}

export function captureProductPageview(url: string, title: string) {
  if (!initialized) return;

  posthog.capture("$pageview", {
    $current_url: url,
    title,
  });
}

export function identifyProductUser(user: ProductAnalyticsUser) {
  if (!initialized) return;

  const personProperties = compactProperties({
    email: user.email,
    name: user.name,
    organization_id: user.organizationId,
    organization_name: user.organizationName,
    role: user.role,
    verified: user.verified,
  });
  const userKey = JSON.stringify({
    distinctId: user.distinctId,
    personProperties,
  });

  if (lastIdentifiedUserKey === userKey) return;

  posthog.identify(user.distinctId, personProperties);
  lastIdentifiedUserKey = userKey;
}

export function identifyProductOrganization(organization: ProductAnalyticsOrganization) {
  if (!initialized) return;

  const organizationKey = JSON.stringify(organization);

  if (lastIdentifiedOrganizationKey === organizationKey) return;

  posthog.group(ORGANIZATION_GROUP_TYPE, organization.id, {
    name: organization.name,
  });
  lastIdentifiedOrganizationKey = organizationKey;
}

export function aliasProductUser(alias: string, original: string) {
  if (!initialized || alias === original) return;

  posthog.alias(alias, original);
}

export function getCurrentProductDistinctId() {
  if (!initialized) return null;

  return posthog.get_distinct_id();
}

export function getCurrentProductUserId() {
  if (!initialized) return null;

  const userId = posthog.get_property("$user_id");

  return typeof userId === "string" ? userId : null;
}

export function resetProductAnalytics() {
  if (!initialized) return;

  posthog.reset();
  lastIdentifiedUserKey = null;
  lastIdentifiedOrganizationKey = null;
}

function compactProperties(properties: EventProperties) {
  return Object.fromEntries(Object.entries(properties).filter(([, value]) => value !== undefined));
}
